/** Taskforce data: types, queries and the calls that change things (all through /api/tf). */
import { useCallback } from 'react';
import { useQueries, useQuery, useQueryClient, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';

export type TimeGroup = 'business' | 'product' | 'engineering';
export interface TfStatus {
  id: string;
  name: string;
  color: string;
  groupId: string;
  position: number;
  isInitial: boolean;
  /** Tasks can start here too (several statuses can; the default starting status always can). */
  isStart: boolean;
  /** Whose time it is, for the time spent on a task (null: overall only). */
  timeGroup: TimeGroup | null;
  isClosed: boolean;
  /** Reaching it puts the task in that day's release. */
  isRelease?: boolean;
  /** People fields that own a task in this status. */
  ownerFieldIds: string[];
  /** A task needs someone in one of these to enter. */
  needFieldIds: string[];
  /** Fields a task needs filled in to enter it (2 Nov 2026); missing from an older server. */
  requiredFieldIds?: string[];
}
export interface TfGroup {
  id: string;
  name: string;
  color: string;
  position: number;
}
export interface TfType {
  id: string;
  name: string;
  color: string;
  icon: string;
  position: number;
}
export interface TfRole {
  id: string;
  key: string;
  name: string;
  group: string | null;
  position: number;
  optional: boolean;
  active: boolean;
  /** Several people (the default) or one person (20 Oct 2026). */
  multiple: boolean;
}
/** links: several links (2 Nov 2026: the PR field). */
export type TfFieldKind = 'select' | 'multi' | 'text' | 'url' | 'number' | 'bool' | 'links';
export interface TfOption {
  id: string;
  name: string;
  color?: string;
  /** A logo (21 Oct 2026), shown instead of the name and colour: /api/tf/icons/:id. */
  icon?: string;
  /** An emoji shown before the name (22 Oct 2026). */
  emoji?: string;
}
/** An option as people read it: its emoji, then its name ("🇦🇪 UAE"). */
export const optionLabel = (o: Pick<TfOption, 'name' | 'emoji'>) => (o.emoji ? `${o.emoji} ${o.name}` : o.name);
/** Where an option's logo is served. */
export const iconUrl = (id: string) => `/api/tf/icons/${id}`;
export interface TfField {
  id: string;
  key: string;
  name: string;
  kind: TfFieldKind;
  options: TfOption[];
  typeIds: string[];
  required: boolean;
  position: number;
  active: boolean;
  system: boolean;
}
export interface TfList {
  id: string;
  spaceId: string;
  name: string;
  tasks: number;
}
export interface TfSpace {
  id: string;
  name: string;
  color: string;
  /** Only Admins and the space's members see it. */
  private: boolean;
  defaultProduct: string | null;
  /** Up to 5 letters or digits: the start of its tasks' references ("BW" in "BW-12"). */
  shortName: string;
  /** The number the space's next task gets. */
  nextRef: number;
  /** A space Task Force keeps itself (22 Oct 2026): the free trial's demo space, or where feedback goes. */
  kind: 'demo' | 'feedback' | null;
  /** People on the free trial can look at it. */
  trialView: boolean;
  /** A copy of an old board's project, kept by the mirror until it is cut over (29 Oct 2026): view only for all. */
  mirrorOf?: string | null;
  mirrorCutAt?: string | null;
  lists: TfList[];
}
export interface TfPerson {
  id: string;
  empNo: number;
  name: string;
  /** For an outside colleague, their company. */
  team: string;
  lead: string;
  /** An outside colleague (29 Oct 2026): from another group company, shown as "Name (Company)". */
  outside?: true;
}
export interface TfMeta {
  groups: TfGroup[];
  statuses: TfStatus[];
  types: TfType[];
  transitions: Array<[string, string, string]>;
  roles: TfRole[];
  fields: TfField[];
  spaces: TfSpace[];
  people: TfPerson[];
  /** The signed-in person's Task Force role (trial: the free trial, 22 Oct 2026). */
  role: 'owner' | 'admin' | 'member' | 'guest' | 'trial';
  /** Spaces this person sees but cannot change: Viewers and Commenters there, and the free trial's (like Legion). */
  readOnly: string[];
  /** Their role in those spaces (23 Oct 2026); Editor in every other space. Missing from an older server. */
  levels?: Record<string, 'view' | 'comment'>;
  /** File attachments: on when storage is set up. */
  attachments: { enabled: boolean; maxMb: number };
  /** Who is signed in, and the reactions comments offer. */
  me: { userId: string; employeeId: string | null };
  reactions: string[];
}
export const isTfAdmin = (m: Pick<TfMeta, 'role'>) => m.role === 'owner' || m.role === 'admin';
/** A space this person can only look at (the free trial's view-only spaces). */
export const isViewOnly = (m: Pick<TfMeta, 'readOnly'>, spaceId: string | null | undefined) =>
  !!spaceId && (m.readOnly ?? []).includes(spaceId);
export const VIEW_ONLY = 'This space is view only on the free trial. Try anything you like in the Demo space.';
/** What someone may do in a space (23 Oct 2026): look, also comment, or anything. */
export type SpaceLevel = 'view' | 'comment' | 'edit';
export const levelIn = (m: Pick<TfMeta, 'readOnly' | 'levels'>, spaceId: string | null | undefined): SpaceLevel =>
  !spaceId ? 'edit' : (m.levels?.[spaceId] ?? (isViewOnly(m, spaceId) ? 'view' : 'edit'));
const RANK: Record<SpaceLevel, number> = { view: 1, comment: 2, edit: 3 };
export const allows = (have: SpaceLevel, need: SpaceLevel) => RANK[have] >= RANK[need];
/** Commenters and Editors comment and add files. */
export const canComment = (m: Pick<TfMeta, 'readOnly' | 'levels'>, spaceId: string | null | undefined) =>
  allows(levelIn(m, spaceId), 'comment');
/** Why a change is not allowed here, in the person's words. */
export const cannotText = (
  m: Pick<TfMeta, 'readOnly' | 'levels' | 'role'> & { spaces?: TfMeta['spaces'] },
  spaceId: string | null | undefined,
) =>
  m.spaces?.some((s) => s.id === spaceId && s.mirrorOf && !s.mirrorCutAt)
    ? 'Kept as the old board has it until it is cut over. Change it on the old board for now.'
    : m.role === 'trial'
      ? VIEW_ONLY
      : levelIn(m, spaceId) === 'comment'
        ? 'You can comment in this space and add files, but not change its tasks.'
        : 'You can view this space, but not change it. Ask an admin for more access.';
/**
 * The spaces of All Tasks: every space seen, except the demo space for people not on the free trial (it is practice,
 * not work; the server leaves it out of search and releases for them too).
 */
export const acrossSpaces = (m: TfMeta) => m.spaces.filter((s) => s.kind !== 'demo' || m.role === 'trial');
export interface TfTask {
  id: string;
  number: number;
  /** "BW-12" */
  ref: string;
  listId: string;
  parentId: string | null;
  typeId: string;
  statusId: string;
  title: string;
  /** Only on a task opened on its own; the views' rows carry `hasDescription` instead (18 Oct 2026). */
  description?: string;
  hasDescription?: boolean;
  priority: number | null;
  startDate: string | null;
  dueDate: string | null;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
  roles: Array<{ fieldId: string; employeeId: string }>;
  values: Array<{ fieldId: string; value: unknown }>;
  _count: { subtasks: number; comments: number; attachments: number };
  /** Each visit to a status, oldest first (time spent per status and group). */
  history: Array<{ statusId: string; enteredAt: string; leftAt: string | null }>;
  /** Where it came from (30 Oct 2026), set when made, never changed; missing from an older server: manual. */
  source?: TfSource;
  /** The form's name, or the MCP app's. */
  sourceDetail?: string | null;
}
export type TfSource = 'manual' | 'form' | 'mcp' | 'clickup';
export interface TfTaskDetail extends TfTask {
  description: string;
  list: { id: string; name: string; space: { id: string; name: string } };
  parent: { id: string; number: number; ref: string; title: string } | null;
  subtasks: TfTask[];
  comments: TfComment[];
  activity: Array<{
    id: string;
    kind: string;
    via: string;
    /** Through the MCP: the connected app (30 Oct 2026). */
    client?: string | null;
    at: string;
    data: Record<string, unknown>;
    actor: { displayName: string } | null;
  }>;
  createdBy: { displayName: string } | null;
  next: TfStatus[];
  /** Required fields still empty (by name). */
  missing: string[];
  /** Phase 3a: who follows it, its checklists, and links to other tasks. */
  watchers: Array<{ userId: string; user: { id: string; displayName: string } }>;
  watching: boolean;
  checklists: TfChecklist[];
  relationsFrom: Array<{ id: string; kind: 'related' | 'blocks'; to: TfRelTask }>;
  relationsTo: Array<{ id: string; kind: 'related' | 'blocks'; from: TfRelTask }>;
  attachments: TfAttachment[];
}
/** A comment or a reply (phase 3c): rich text, reactions, and an optional assignment to resolve. */
export interface TfComment {
  id: string;
  parentId: string | null;
  body: string;
  mentions: string[];
  createdAt: string;
  editedAt: string | null;
  author: { id: string; displayName: string } | null;
  assignee: { id: string; name: string } | null;
  assignedBy: { displayName: string } | null;
  resolvedAt: string | null;
  resolvedBy: { displayName: string } | null;
  reactions: Array<{ emoji: string; userId: string; user: { displayName: string } }>;
  /** Written through the MCP (30 Oct 2026), and with which connected app. */
  via?: string;
  client?: string | null;
}
export interface TfAttachment {
  id: string;
  name: string;
  size: number;
  type: string;
  createdAt: string;
  uploadedBy: string | null;
  uploader: { displayName: string } | null;
}
export interface TfRelTask {
  id: string;
  number: number;
  /** "BW-12" */
  ref: string;
  title: string;
  statusId: string;
  listId: string;
}
export interface TfChecklistItem {
  id: string;
  parentId: string | null;
  text: string;
  done: boolean;
  employeeId: string | null;
  position: number;
}
export interface TfChecklist {
  id: string;
  name: string;
  items: TfChecklistItem[];
}

/** The tasks on screen, in order (the task view's ↑ / ↓ walk it). */
let order: string[] = [];
export const setTaskOrder = (refs: string[]) => {
  order = refs;
};
export const neighbours = (ref: string): [string | null, string | null] => {
  const i = order.indexOf(ref);
  return i < 0 ? [null, null] : [order[i - 1] ?? null, order[i + 1] ?? null];
};

export const PRIORITIES = [
  { v: 0, label: 'Urgent', color: '#d33d44' },
  { v: 1, label: 'High', color: '#f8ae00' },
  { v: 2, label: 'Normal', color: '#4466ff' },
  { v: 3, label: 'Low', color: '#87909e' },
];

/** The default order inside a status (21 Oct 2026): Urgent, High, Normal, Low, then no priority; otherwise as they were. */
export const byPriority = (a: { priority: number | null }, b: { priority: number | null }) =>
  (a.priority ?? 9) - (b.priority ?? 9);

export const useTfMeta = () => useQuery({ queryKey: ['tf', 'meta'], queryFn: () => api<TfMeta>('/api/tf/meta') });
/**
 * Every task in a space (tasks show directly in the space; its list is kept out of sight). Closed tasks older than 30
 * days load only with "Show closed" on; while they load, the recent ones stay on screen. "all" (the sidebar's All
 * Tasks) is every space's list put together here (18 Oct 2026): each comes from the server's shared copy and live
 * updates keep each one current, so a change in one space never makes All Tasks load everything again.
 */
/** This tab's live stream (set by useLiveUpdates), sent when a space's list loads so its changes come with rows. */
export const liveStream = { id: null as string | null };

export function useTfTasks(spaceId: string | null, allClosed = false) {
  const meta = useTfMeta();
  const all = spaceId === 'all';
  // meta lists the spaces this person sees, archived ones left out.
  const ids = all ? (meta.data ? acrossSpaces(meta.data).map((s) => s.id) : []) : spaceId ? [spaceId] : [];
  const combine = useCallback(
    (rs: Array<UseQueryResult<TfTask[]>>) => ({
      data:
        rs.length && rs.every((r) => r.data)
          ? rs.length === 1
            ? rs[0]!.data
            : rs.flatMap((r) => r.data!)
          : all && meta.data && !rs.length
            ? []
            : undefined,
      isLoading: (all && !meta.data) || rs.some((r) => r.isLoading),
      isError: rs.some((r) => r.isError),
    }),
    [all, meta.data],
  );
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: ['tf', 'tasks', id, allClosed ? 'all' : 'recent'],
      queryFn: () =>
        api<{ tasks: TfTask[] }>(`/api/tf/spaces/${id}/tasks${allClosed ? '?closed=all' : ''}`, {
          headers: !all && liveStream.id ? { 'X-Tf-Stream': liveStream.id } : undefined,
        }).then((r) => r.tasks),
      placeholderData: (prev: TfTask[] | undefined, prevQuery?: { queryKey: readonly unknown[] }) =>
        prevQuery?.queryKey[2] === id ? prev : undefined,
    })),
    combine,
  });
}
/**
 * A task as far as a list on screen already knows it (22 Oct 2026): the task view shows this at once (name, status,
 * people, fields) while the rest (description, comments, subtasks …) loads. `partial` marks what is missing.
 */
function draftTask(qc: QueryClient, ref: string): (TfTaskDetail & { partial: true }) | undefined {
  const want = ref.toLowerCase();
  let row: TfTask | undefined;
  for (const q of qc.getQueryCache().findAll({ queryKey: ['tf', 'tasks'] })) {
    row = (q.state.data as TfTask[] | undefined)?.find((t) => t.ref.toLowerCase() === want || t.id === ref);
    if (row) break;
  }
  const meta = qc.getQueryData<TfMeta>(['tf', 'meta']);
  const space = meta?.spaces.find((s) => s.lists.some((l) => l.id === row?.listId));
  const list = space?.lists.find((l) => l.id === row?.listId);
  if (!row || !meta || !space || !list) return undefined;
  const next = nextStatuses(meta, row.typeId, row.statusId);
  return {
    ...row,
    description: '',
    list: { id: list.id, name: list.name, space: { id: space.id, name: space.name } },
    parent: null,
    subtasks: [],
    comments: [],
    activity: [],
    createdBy: null,
    next: meta.statuses.filter((s) => next.has(s.id)),
    missing: [],
    watchers: [],
    watching: false,
    checklists: [],
    relationsFrom: [],
    relationsTo: [],
    attachments: [],
    partial: true,
  };
}
export const isPartial = (t: TfTaskDetail) => (t as { partial?: boolean }).partial === true;

export function useTfTask(ref: string | null) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ['tf', 'task', ref],
    placeholderData: () => (ref ? draftTask(qc, ref) : undefined),
    queryFn: async () => {
      const t = await api<TfTaskDetail>(`/api/tf/tasks/${encodeURIComponent(ref!)}`, {
        // Its changes come to this tab live, wherever the task is.
        headers: liveStream.id ? { 'X-Tf-Stream': liveStream.id } : undefined,
      });
      // Opening a task reads what the Inbox had about it.
      void qc.invalidateQueries({
        predicate: (q) => q.queryKey[0] === 'tf' && String(q.queryKey[1]).startsWith('inbox'),
      });
      return t;
    },
    enabled: !!ref,
  });
}

/** After any change: refresh what may show it (the list, the open task, counts in the sidebar). */
/** A change to a task's row, shown before the server has answered. */
export type RowChange = (t: TfTask) => TfTask;

/** Every list and board here takes a change to a task at once (22 Oct 2026); the live update then settles it. */
export function patchTask(qc: QueryClient, id: string, change: RowChange) {
  for (const q of qc.getQueryCache().findAll({ queryKey: ['tf', 'tasks'] }))
    qc.setQueryData<TfTask[]>(q.queryKey, (old) => old?.map((t) => (t.id === id ? change(t) : t)));
}
export const withValue =
  (fieldId: string, value: unknown): RowChange =>
  (t) => ({
    ...t,
    values: [...t.values.filter((v) => v.fieldId !== fieldId), ...(isEmpty(value) ? [] : [{ fieldId, value }])],
  });
export const withPeople =
  (fieldId: string, ids: string[]): RowChange =>
  (t) => ({
    ...t,
    roles: [...t.roles.filter((r) => r.fieldId !== fieldId), ...ids.map((employeeId) => ({ fieldId, employeeId }))],
  });

/**
 * Saving a change to a task from a list, board or table (22 Oct 2026). With `change`, the rows show it at once and the
 * save goes on behind: the server's live update brings the saved row back, so nothing is fetched again (before, every
 * edit fetched every Task Force answer anew, a whole space's tasks among them). A failure says why and puts the rows
 * back as the server has them. Without `change` (moves that the workflow decides), the lists are fetched again.
 */
export function useTaskSave(onError: (message: string) => void) {
  const qc = useQueryClient();
  return useCallback(
    async (t: TfTask, fn: () => Promise<unknown>, change?: RowChange) => {
      if (change) patchTask(qc, t.id, change);
      try {
        await fn();
        if (!change || !liveStream.id) await qc.invalidateQueries({ queryKey: ['tf'] });
      } catch (e) {
        // A move called off in the fields' dialog (gates.tsx): put back, nothing to say.
        if (!(e instanceof ApiError && e.code === 'CANCELLED'))
          onError(e instanceof ApiError ? e.message : 'Could not save that change.');
        await qc.invalidateQueries({ queryKey: ['tf', 'tasks'] });
      }
    },
    [qc, onError],
  );
}

export function useTfRefresh() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['tf'] });
}

/** The statuses a task may move to next, from the workflow for its type. */
export function nextStatuses(meta: TfMeta, typeId: string, statusId: string): Set<string> {
  return new Set(meta.transitions.filter(([t, f]) => t === typeId && f === statusId).map(([, , to]) => to));
}

export const initials = (name: string) =>
  name
    .split(/\s+/)
    // Words only ("Omar · Backend" is OB).
    .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
export const fmtDay = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }) : '';
/** A people field as people see it: its own name only (20 Oct 2026; the group stays in Settings). */
export const roleLabel = (r: TfRole) => r.name;

/** The fields that show on a task of this type, in order. */
export const fieldsFor = (meta: TfMeta, typeId: string) =>
  meta.fields.filter((f) => f.active && (!f.typeIds.length || f.typeIds.includes(typeId)));
export const isEmpty = (v: unknown) => v == null || v === '' || (Array.isArray(v) && v.length === 0);
export const valueOf = (t: Pick<TfTask, 'values'>, fieldId: string) =>
  t.values.find((v) => v.fieldId === fieldId)?.value;
/** A value as people read it: option names, Yes/No. */
export function showValue(f: TfField, v: unknown): string {
  if (isEmpty(v)) return '';
  const name = (id: unknown) => {
    const o = f.options.find((x) => x.id === id);
    return o ? optionLabel(o) : String(id);
  };
  // A field switched between one and several options may still hold the other shape: both read.
  if (f.kind === 'select' || f.kind === 'multi') return (Array.isArray(v) ? v : [v]).map(name).join(', ');
  if (f.kind === 'bool') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.map(String).join(', ');
  return String(v);
}
/** The statuses a task can start in, the default first. */
export const startStatuses = (meta: TfMeta) =>
  meta.statuses
    .filter((s) => (s.isInitial || s.isStart) && !s.isClosed)
    .sort((a, b) => Number(b.isInitial) - Number(a.isInitial) || a.position - b.position);
/** Who owns a task now: the people in its status's owner fields. */
export function ownersOf(meta: TfMeta, t: Pick<TfTask, 'statusId' | 'roles'>): string[] {
  const s = meta.statuses.find((x) => x.id === t.statusId);
  if (!s) return [];
  return [...new Set(t.roles.filter((r) => s.ownerFieldIds.includes(r.fieldId)).map((r) => r.employeeId))];
}
/** Why a task cannot enter a status yet (the people it needs), or '' when it can. */
export function gateOf(meta: TfMeta, t: Pick<TfTask, 'roles'>, to: TfStatus): string {
  if (!to.needFieldIds.length || t.roles.some((r) => to.needFieldIds.includes(r.fieldId))) return '';
  const need = meta.roles.filter((r) => to.needFieldIds.includes(r.id)).map(roleLabel);
  const names = need.length > 1 ? `${need.slice(0, -1).join(', ')} or ${need[need.length - 1]}` : need[0];
  return `Assign ${names ?? 'someone'} first`;
}

/** A saved List or Board view of a space ("all" for All Tasks). */
/** The kinds of view a space has (phase 5 added Table and Calendar). */
export type TfViewKind = 'list' | 'board' | 'table' | 'calendar';
export const VIEW_KINDS: Array<[TfViewKind, string]> = [
  ['list', 'List'],
  ['board', 'Board'],
  ['table', 'Table'],
  ['calendar', 'Calendar'],
];
export interface TfView {
  id: string;
  spaceKey: string;
  name: string;
  kind: TfViewKind;
  config: Record<string, unknown>;
  ownerId: string;
  shared: boolean;
  isDefault: boolean;
  /** Made by the signed-in person. */
  mine: boolean;
}
export const useTfViews = (spaceKey: string) =>
  useQuery({
    queryKey: ['tf', 'views', spaceKey],
    queryFn: () => api<TfView[]>(`/api/tf/views?space=${encodeURIComponent(spaceKey)}`),
  });

// ------------------------------------------------------------------ Inbox (phase 4)

export type TfInboxTab = 'primary' | 'other' | 'later' | 'cleared';
export interface TfInboxItem {
  id: string;
  /** mention | assigned | reply | people | resolved | watching */
  reason: string;
  primary: boolean;
  createdAt: string;
  readAt: string | null;
  snoozedUntil: string | null;
  clearedAt: string | null;
  activity: {
    kind: string;
    data: Record<string, unknown>;
    at: string;
    via: string;
    client?: string | null;
    actor: { displayName: string } | null;
  };
  task: {
    id: string;
    number: number;
    /** "BW-12" */
    ref: string;
    title: string;
    status: { name: string; color: string };
    space: { id: string; name: string };
  };
}
export const useTfInbox = (tab: TfInboxTab) =>
  useQuery({ queryKey: ['tf', 'inbox', tab], queryFn: () => api<TfInboxItem[]>(`/api/tf/inbox?tab=${tab}`) });
/** Unread in Primary and Other: the sidebar's count. */
export const useTfInboxCounts = () =>
  useQuery({
    queryKey: ['tf', 'inbox-counts'],
    queryFn: () => api<{ primary: number; other: number }>('/api/tf/inbox/counts'),
  });

// ------------------------------------------------------------------ Assigned Comments and Replies (phase 4)

/** A comment with its whole thread and the task it is on. */
export interface TfThread {
  comment: TfComment;
  replies: TfComment[];
  task: TfInboxItem['task'];
}
export const useTfAssigned = (resolved: boolean) =>
  useQuery({
    queryKey: ['tf', 'my-assigned', resolved],
    queryFn: () => api<TfThread[]>(`/api/tf/my/assigned-comments${resolved ? '?resolved=1' : ''}`),
  });
export const useTfReplies = () =>
  useQuery({ queryKey: ['tf', 'my-replies'], queryFn: () => api<TfThread[]>('/api/tf/my/replies') });
/** Comments waiting on the signed-in person: the sidebar's count. */
export const useTfMyCounts = () =>
  useQuery({ queryKey: ['tf', 'my-counts'], queryFn: () => api<{ assigned: number }>('/api/tf/my/counts') });
/** My Tasks: the tasks I work on, and the ones I created or requested (closed ones for 30 days). */
export const useTfMyTasks = () =>
  useQuery({
    queryKey: ['tf', 'my-tasks'],
    queryFn: () =>
      api<{ mine: Array<TfTask & { spaceId: string }>; delegated: Array<TfTask & { spaceId: string }> }>(
        '/api/tf/my/tasks',
      ),
  });
/** The signed-in person's notification settings. */
export interface TfNotifyPrefs {
  inbox: 'all' | 'focused' | 'custom';
  kinds: string[];
  browser: boolean;
  followOnComment: boolean;
  followOnCreate: boolean;
  /** Email (18 Oct 2026): the master switch, the events emailed, and holding emails outside working hours. */
  emailOn: boolean;
  email: string[];
  /** Push (later): the events pushed. */
  push: string[];
  quiet: boolean;
  /** The events to choose from, and whether Task Force sends emails at all yet (read only). */
  events: Array<{ key: string; label: string; group: 'me' | 'follow' }>;
  emailsOn: boolean;
}
export const useTfNotifyPrefs = () =>
  useQuery({ queryKey: ['tf', 'inbox-settings'], queryFn: () => api<TfNotifyPrefs>('/api/tf/inbox/settings') });

// ------------------------------------------------------------------ task templates (phase 5)

export interface TfTemplate {
  id: string;
  name: string;
  spaceId: string | null;
  typeId: string;
  createdAt: string;
  createdById: string | null;
  createdBy: { displayName: string } | null;
  space: { name: string } | null;
  data: {
    description: string;
    priority: number | null;
    values: Record<string, unknown>;
    roles: Record<string, string[]>;
    checklists: Array<{ name: string; items: unknown[] }>;
    subtasks: string[];
  };
}
export const useTfTemplates = () =>
  useQuery({ queryKey: ['tf', 'templates'], queryFn: () => api<TfTemplate[]>('/api/tf/templates') });

/**
 * How a change or comment came in, when not in the app (30 Oct 2026): "via Claude (MCP)", "via form", and a webhook's
 * answer (2 Nov 2026) "via webhook".
 */
export const viaLabel = (via: string | undefined, client?: string | null) =>
  via === 'mcp'
    ? client
      ? `via ${client} (MCP)`
      : 'via MCP'
    : via === 'form'
      ? 'via form'
      : via === 'hook'
        ? 'via webhook'
        : null;
